import { apiClient } from '@/lib/api-client';
import type { FleetMetrics, PlatformOverview, ServerMetrics } from '@/types/api';

export const FLEET_METRICS_KEY = ['metrics', 'fleet'] as const;
export const platformOverviewKey = (hours: number) => ['admin', 'overview', hours] as const;
export const serverMetricsKey = (id: string, hours: number) =>
  ['metrics', 'server', id, hours] as const;

export const metricsApi = {
  /** Every server the customer owns, with its latest reading and a short sparkline. */
  fleet: () => apiClient.get<FleetMetrics>('/metrics/servers'),

  /** One server's history. `hours` is clamped server-side to the retention horizon. */
  server: (id: string, hours: number) =>
    apiClient.get<ServerMetrics>(`/metrics/servers/${id}?hours=${hours}`),

  /**
   * The operator's own platform. Staff only — the handler re-checks the role, so a customer
   * calling this gets a 403 rather than platform-wide counts.
   */
  platformOverview: (hours: number) =>
    apiClient.get<PlatformOverview>(`/admin/overview?hours=${hours}`),
};

/** Bytes per second, for network rates. */
export function formatRate(bytesPerSecond: number | null): string {
  if (bytesPerSecond === null) return 'no reading';
  if (bytesPerSecond < 1024) return `${bytesPerSecond} B/s`;
  if (bytesPerSecond < 1048576) return `${(bytesPerSecond / 1024).toFixed(1)} KB/s`;

  return `${(bytesPerSecond / 1048576).toFixed(2)} MB/s`;
}

export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1048576) return `${(bytes / 1024).toFixed(0)} KB`;
  if (bytes < 1073741824) return `${(bytes / 1048576).toFixed(0)} MB`;

  return `${(bytes / 1073741824).toFixed(1)} GB`;
}

/** Uptime, at the granularity a person actually cares about. */
export function formatUptime(seconds: number | null): string {
  if (seconds === null || seconds <= 0) return 'Not reported';

  const days = Math.floor(seconds / 86400);
  const hours = Math.floor((seconds % 86400) / 3600);

  if (days > 0) return `${days}d ${hours}h`;

  const minutes = Math.floor((seconds % 3600) / 60);

  return hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`;
}
